import { memo, useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { FieldItem } from './field-item';
import { FieldsContainerProps } from './types';
import { getFieldItemId, getFieldSortableIds } from './dnd';

export const FieldsContainer = memo(
  ({
    name,
    fields,
    onRemove,
    simplified,
    onCopyField,
    allowInputId,
    activeFieldId,
    enableSorting,
    allowOutcomes,
    allowPageBreak,
    allowFieldArray,
    onPasteFieldAfter,
    allowOutcomeFields,
    allowReferenceCodes,
    outcomeFieldSources,
  }: FieldsContainerProps) => {
    const sortableIds = useMemo(() => getFieldSortableIds(fields), [fields]);
    const canDeleteFields = fields.length > 1;
    const { isOver, setNodeRef } = useDroppable({
      id: name,
      data: {
        containerId: name,
        type: 'field-container',
      },
    });

    return (
      <div
        ref={setNodeRef}
        data-testid={`field-container-${name}`}
        className={`flex flex-col gap-4 rounded-lg border border-dashed p-3 transition-colors ${
          isOver
            ? 'border-primary bg-gray-50 dark:bg-[#101318]'
            : 'border-gray-200 dark:border-gray-700'
        }`}
      >
        <SortableContext
          id={name}
          items={sortableIds}
          strategy={verticalListSortingStrategy}
        >
          {fields.map((value, index) => {
            const sortableId = getFieldItemId(value.id);

            return (
              <FieldItem
                value={value}
                index={index}
                key={value.id}
                onRemove={onRemove}
                containerPath={name}
                simplified={simplified}
                name={`${name}.${index}`}
                allowInputId={allowInputId}
                canDelete={canDeleteFields}
                allowOutcomes={allowOutcomes}
                allowPageBreak={allowPageBreak}
                allowFieldArray={allowFieldArray}
                allowOutcomeFields={allowOutcomeFields}
                allowReferenceCodes={allowReferenceCodes}
                outcomeFieldSources={outcomeFieldSources}
                isDragging={activeFieldId === sortableId}
                sortableId={enableSorting ? sortableId : undefined}
                onCopy={onCopyField ? () => onCopyField(value) : undefined}
                onPasteAfter={
                  onPasteFieldAfter ? () => onPasteFieldAfter(index) : undefined
                }
              />
            );
          })}
        </SortableContext>
      </div>
    );
  }
);

FieldsContainer.displayName = 'FieldsContainer';
